import React, { memo, useContext, useCallback, useRef } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { pushWithCruiseFilters } from 'actions/CruiseActions'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Pane from 'components/Common/Pane'
import TextButton from 'components/Luxkit/Button/TextButton'
import CruiseSearchForm from './Inputs/CruiseSearchInputs'
import useQueryParams from 'hooks/useQueryParams'
import * as Analytics from 'analytics/analytics'
import GobalSearchState from 'contexts/GlobalSearch/GlobalSearchState'
import { searchFromHomepageEvent } from 'analytics/optimizely/events'

const CruiseSearchFiltersBarGrid = styled.div`
  display: grid;
  gap: ${rem(8)};
  grid-template-columns: 1fr;

  ${mediaQueryUp.tablet} {
    gap: ${rem(16)};
    grid-template-columns: 1fr 1fr 1fr 1fr ${rem(112)};
  }
`

const SearchButton = styled(TextButton)`
  height: 100%;
`

interface Props {
  forceSearchPage?: boolean;
}

function CruiseSearchFiltersBar({ forceSearchPage }: Props) {
  const dispatch = useAppDispatch()
  const globalSearch = useContext(GlobalSearchStateContext)
  const pathname = useAppSelector(state => state.router.location.pathname)
  const regionCode = useAppSelector(state => state.geo.currentRegionCode)
  const queryParams = useQueryParams()
  const searchBarRef = useRef<HTMLDivElement>(null)

  // update url when clicking search button
  const onSubmit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (pathname === `/${regionCode.toLowerCase()}`) {
      Analytics.track('search-from-homepage', searchFromHomepageEvent())
    }

    dispatch(pushWithCruiseFilters('/search/cruises', globalSearch, queryParams))
  }, [dispatch, globalSearch, pathname, regionCode, queryParams])

  const onChange = useCallback((values : App.CruiseGlobalFilters) => {
    if (pathname === `/${regionCode.toLowerCase()}`) {
      Analytics.track('search-from-homepage', searchFromHomepageEvent())
    }

    const {
      searchItem,
      secondarySearchItem,
      flexibleMonths,
      durationMax,
      durationMin,
      cruiseLines,
    } = values

    const filters: App.CruiseGlobalFilters = {
      ...globalSearch,
      ...(cruiseLines && { cruiseLines }),
    }

    if ('durationMin' in values) {
      filters.durationMin = durationMin
    }

    if ('durationMax' in values) {
      filters.durationMax = durationMax
    }

    if ('flexibleMonths' in values) {
      filters.flexibleMonths = flexibleMonths
    }

    if ('searchItem' in values) {
      filters.searchItem = searchItem
    }

    if ('secondarySearchItem' in values) {
      filters.secondarySearchItem = secondarySearchItem
    }

    const pageName = forceSearchPage ? '/search/cruises' : pathname.replace(`/${regionCode.toLowerCase()}`, '')
    dispatch(pushWithCruiseFilters(pageName, filters as GobalSearchState, queryParams))
  }, [dispatch, globalSearch, pathname, regionCode, queryParams, forceSearchPage])

  return <Pane ref={searchBarRef}>
    <CruiseSearchFiltersBarGrid
      as="form"
      onSubmit={onSubmit}
    >
      <CruiseSearchForm
        onChange={onChange}
        dropdownAnchorRef={searchBarRef}
      />

      <SearchButton
        kind="primary"
        type="submit"
        size="large"
      >
        Search
      </SearchButton>
    </CruiseSearchFiltersBarGrid>
  </Pane>
}

export default memo(CruiseSearchFiltersBar)
